import '../LocalPage.css';
import React from 'react';
import { Button, Alert, DropdownButton, Dropdown, ProgressBar } from 'react-bootstrap';
import { connect } from 'react-redux';
import * as sagas from '../../store/sagas';
import RTViewer from '../rtviewer/RTViewer';
import { StoreState } from '../../store/store';

import { isDemo } from '../../environments';
import { rtViewerApiClient } from '../../web-apis/rtviewer-api-client';
import { RouteComponentProps } from 'react-router';
import { StructureSet } from '../../dicom/structure-set';
import { FileLoadError } from '../../store/file-load-error';
import { User } from '../../store/user';
import WorkState, { Workspace } from '../../store/work-state';
import AccessForbidden from '../common/AccessForbidden';
import { sleep } from '../../util';
import _ from 'lodash';
import { Scan } from '../../store/scans';
import { LocalFileProgress } from '../../store/file-transfer-task';
import { NotificationType, SessionNotification } from '../common/models/SessionNotification';
import { getIsAutoOpenScanOnFileLoadEnabled, setIsAutoOpenScanOnFileLoadEnabled } from '../../local-storage';

import './VerifyPage.css';

type OwnProps = {}
type DispatchProps = {
    unloadAllScans(): void,
    unloadStructureSet(scanId: string, structureSetId: string): void,
    storeLocalScan(files: File[], cbReturnId: (scanId: string) => void): void,
    storeLocalStructureSet(file: File, currentScanId: string, cbReturnId: (scanId: string, ssId: string, structureSet: StructureSet) => void): void,
    clearFileLoadErrors(): void,
    updateCurrentWorkState(updatedWorkStateProps: Partial<WorkState>): void,
    addNotification(notification: SessionNotification, delayInMilliseconds?: number): void,
    dismissNotification(notificationId: string): void,
}

type AllProps = OwnProps & StoreState & DispatchProps & RouteComponentProps;
type OwnState = {
    showRTViewer: boolean,
    immediatelyOpenScan: boolean,
    scanId?: string | null,
    structureSetId?: string | null,
}

const PREVENT_OPEN_IF_ERRORS = false;

const ERROR_MESSAGE_BUFFER = 'Request more than currently allocated buffer';

export class VerifyPage extends React.Component<AllProps, OwnState> {
    displayName = VerifyPage.name

    constructor(props: AllProps) {
        super(props);

        this.state = { showRTViewer: false, immediatelyOpenScan: true };
    }

    componentDidMount = () => {
        // make sure correct workspace is set
        if (this.props.currentWorkState && this.props.currentWorkState.workspace !== Workspace.QATool) {
            this.props.updateCurrentWorkState({ workspace: Workspace.QATool });
        }

        const isAutoOpenScanOnFileLoadEnabled = getIsAutoOpenScanOnFileLoadEnabled();
        if (isAutoOpenScanOnFileLoadEnabled !== undefined) {
            this.setState({ immediatelyOpenScan: isAutoOpenScanOnFileLoadEnabled });
        }
    }

    openScan = async (scanId: string, noWait: boolean = false) => {
        // sleep for a bit to let progress bars etc finish
        if (!noWait) {
            await sleep(500);
        }
        this.setState({ scanId: scanId });
        if (this.state.immediatelyOpenScan && (!this.props.fileLoadErrors || this.props.fileLoadErrors.length === 0) && _.isObject(this.props.scans) && Object.keys(this.props.scans).length === 1) {
            this.setState({ showRTViewer: true });
        }
    }

    openStructureSet = (scanId: string, structureSetId: string) => {
        if (this.state.scanId !== scanId) {
            alert("Structure set doesn't belong to current image");
        }
        else {
            this.setState({ structureSetId: structureSetId });
        }
    }

    handleImageFileChange = (e: any) => {
        this.props.clearFileLoadErrors();
        this.props.unloadAllScans();
        e.persist();
        this.setState({ scanId: null, structureSetId: null }, () => {
            let files = [];
            for (let i = 0; i < e.target.files.length; i++) {
                files.push(e.target.files[i])
            }
            if (files.length > 0) {
                this.props.storeLocalScan(files, this.openScan);
            }
        });
    }

    handleStructureSetFileChange = (e: any) => {
        if (!this.state.scanId) return;
        let scanId: string = this.state.scanId;

        if (this.state.structureSetId) {
            this.props.unloadStructureSet(scanId, this.state.structureSetId);
        }
        e.persist();
        this.setState({ structureSetId: null }, () => {
            this.props.storeLocalStructureSet(e.target.files[0], scanId, this.openStructureSet);
        })
    }

    handleShowRTViewerClick = () => {
        const scans = this.props.scans ? Object.values(this.props.scans) : [];
        // First find current scan
        const currentScan = scans.find(scan => scan.scanId === this.state.scanId);
        if (!currentScan) {
            this.props.addNotification(new SessionNotification(
                'scan-not-found',
                'Error: Please select a scan.',
                NotificationType.Warning,
                undefined,
                5000
            ));
            return;
        }
        // Check if current scan has structure sets but no slices
        const hasStructureSets = currentScan.structureSets && Object.keys(currentScan.structureSets).length > 0;
        const hasNoSlices = !currentScan.slices || Object.keys(currentScan.slices).length === 0;

        if (hasStructureSets && hasNoSlices) {
            this.props.addNotification(new SessionNotification(
                'scan-not-found-ss',
                'Error: Please select a scan with slices.',
                NotificationType.Warning,
                undefined,
                5000
            ));
            return;
        }

        this.setState({ showRTViewer: true });
    }

    handleBack = () => {
        this.setState({ showRTViewer: false });
    }

    handleImmediatelyOpenChange = () => {
        setIsAutoOpenScanOnFileLoadEnabled(!this.state.immediatelyOpenScan);
        this.setState({ immediatelyOpenScan: !this.state.immediatelyOpenScan });
    }

    getScanTitle = (scan: Scan): string => {
        const { seriesDescription, protocolName, studyDescription, bodyPartExamined, } = scan;

        let scanName = '<Scan>';

        if (_.isString(seriesDescription) && seriesDescription.trim() !== '') { scanName = seriesDescription; }
        else if (_.isString(protocolName) && protocolName.trim() !== '') { scanName = protocolName; }
        else if (_.isString(studyDescription) && studyDescription.trim() !== '') { scanName = studyDescription; }
        else if (_.isString(bodyPartExamined) && bodyPartExamined.trim() !== '') { scanName = bodyPartExamined; }

        if (_.has(scan, 'modality')) {
            scanName = `${scanName} (${scan.modality})`;
        }
        return `${scanName}   `;
    }

    handleSelectScan = (scan: Scan) => {
        this.openScan(scan.scanId, true);
    }

    renderScanSelector = () => {
        const scans = this.props.scans ? Object.values(this.props.scans) : [];
        if (scans.length < 2) {
            return null;
        }
        else {
            const scanIndex = scans.findIndex(s => s.scanId === this.state.scanId);
            const currentScan = scans[scanIndex];
            const dropdownTitle = currentScan ? `Scan #${scanIndex + 1} -- ${this.getScanTitle(currentScan)}` : 'Select scan';
            return (
                <>
                    <div className="rtstruct-form info1"><p>3. Select scan to open:</p></div>
                    <DropdownButton id="scan-selector" title={dropdownTitle} variant="outline-info" size="sm">
                        {
                            scans.map((scan: any, index) =>
                                <Dropdown.Item as="button" key={scan.scanId} onClick={() => this.handleSelectScan(scan)}>
                                    Scan #{index + 1} -- {this.getScanTitle(scan)}
                                </Dropdown.Item>
                            )}
                    </DropdownButton>
                </>
            );
        }
    }

    renderFileForm = (isVisible: boolean) => {

        const { fileLoadErrors, user, localFileProgress }: { fileLoadErrors?: FileLoadError[], user?: User, localFileProgress?: LocalFileProgress } = this.props;

        if (!user || !user.permissions.qaToolAccess) {
            setTimeout(() => {
                this.props.history.push('/');
                window.location.reload();
            }, 3500);
            return <AccessForbidden />;
        }

        const isOpenPrevented = PREVENT_OPEN_IF_ERRORS && (fileLoadErrors !== undefined && fileLoadErrors.length > 0);

        // by hiding instead of not rendering the file form we're able to save the state of the browser's built-in
        // file load forms, allowing us to show the number and names of files loaded in the UI after user presses
        // back on the main rtviewer view
        const hiddenStyle = isVisible ? '' : 'hide-file-form';

        const multipleAvailableScans = this.props.scans && Object.keys(this.props.scans).length > 1;
        const immediatelyOpenScanState = this.state.immediatelyOpenScan && !multipleAvailableScans;

        return (
            <>
                <div className={`local-page-container delineation-page ${hiddenStyle}`}>
                    <div className='left flex'>
                        <div className='description-text'>
                            <img src={"/img/verify-title-new.svg"} alt="logo" className="logo-img" />
                            <h2 style={{ marginBottom: "0.5em", color: "#02b1c0", width: "80%" }}>Contour Comparison Tool</h2>

                            <h5 style={{ marginBottom: "0.5em", color: "#f49800", width: "80%" }}>Visual and quantitative comparison of regions-of-interest</h5>

                            <p className='info1'>MVision Verify can be used to visualize differences between two structures or calculate similarity metrics between two structure sets.</p>
                            <p className='info1'>When calculating similarity metrics the structures under comparison must have identical names.</p>
                            <p className='info1'>Structure sets are anonymized before sending them to the similarity metrics calculation service.</p>

                            <h4 style={{ marginTop: "1.3em", marginBottom: "0.5em", color: "#02b1c0", width: "50%" }}>Select images and associated structure sets for comparison</h4>

                            <div className="image-form info1">
                                <p>Select DICOM files (CT/MR and structure sets):</p>
                                <br />
                                <div className="dark-file-input-container">
                                    <label className="dark-file-input-label turquoise">
                                        Select files
                                    <input
                                            type="file"
                                            className="dark-file-input"
                                            onChange={this.handleImageFileChange}
                                            multiple
                                        />
                                    </label>
                                </div>

                            </div>
                            <div>
                                <input
                                    name="OpenFileSelection"
                                    id="OpenFileSelection"
                                    type="checkbox"
                                    checked={immediatelyOpenScanState}
                                    onChange={this.handleImmediatelyOpenChange}
                                    disabled={multipleAvailableScans} />
                                <label htmlFor="OpenFileSelection" className="open-file-selection-check-label">Immediately open selected files</label>
                            </div>

                            {localFileProgress && (<div className="scan-progress-bar"><ProgressBar variant="info" now={localFileProgress.progress} max={localFileProgress.total} /></div>)}

                            {this.state.scanId ? <div className="rtstruct-form info1"><p>Select a DICOM structure set file from another location (optional):</p>
                                <div className="dark-file-input-container">
                                    <label className="dark-file-input-label turquoise" style={{
                                        width: "max-content"
                                    }}>
                                        Add file<input type="file" ref="structureSetInput"
                                            className="dark-file-input" onChange={this.handleStructureSetFileChange} />
                                    </label>
                                </div></div> : null
                            }
                            {this.state.scanId && !isOpenPrevented ?
                                <div>
                                    <span>{this.renderScanSelector()}</span>
                                    <span>
                                        <Button
                                            variant="light"
                                            className="open-rtviewer-button btn btn-default col-card-btn turquoise"
                                            onClick={this.handleShowRTViewerClick}>
                                            <img width="23" style={{ marginRight: '2rem' }} src="/img/icon-quality-assurance-sm.svg" alt="Verify" />
                                            <span>Open MVision Verify</span>
                                        </Button>
                                    </span>
                                </div>

                                : null
                            }
                        </div>
                        <br />

                        <div className='right'>
                            <img src={"/img/verify-monitor.svg"} alt="logo" className="example-img" />
                        </div>

                        {fileLoadErrors && fileLoadErrors.length > 0 && (
                            <Alert variant="warning" className="file-load-errors-alert">
                                <Alert.Heading>{fileLoadErrors.length !== 1 ? 'File load errors' : 'File load error'}</Alert.Heading>
                                <div>Following files could not be loaded and were ignored.</div>
                                {fileLoadErrors.map(e => (
                                    <div key={e.fileName} className="file-load-errors">
                                        <div className="filename"><b>{e.fileName}</b></div>
                                        <div className="error-message">{e.errorMessage === ERROR_MESSAGE_BUFFER ? 'File format is not supported.' : e.errorMessage}</div>
                                    </div>
                                ))}
                            </Alert>
                        )}
                    </div>
                </div>
                {!this.state.showRTViewer &&
                    <div className="footer-apps">Verify is part of MVision AI GBS™</div>
                }

            </>
        );
    }

    render() {
        const user = rtViewerApiClient.username;
        const scanId = this.state.scanId;
        const canEdit = !isDemo() ||
            (user === "demo-strang1" || user === "demo-strang2" || user === "demo-berlin1");

        return (
            <>
                {this.renderFileForm(!(this.state.showRTViewer && scanId))}
                {this.state.showRTViewer && scanId &&
                    (
                        <RTViewer scanId={scanId}
                            canEdit={canEdit} canCreateRtstruct={true} hideSaveButtons={true} handleBack={this.handleBack} history={this.props.history} location={this.props.location}
                            match={this.props.match} />
                    )}
            </>
        );
    }
}

export default connect(
    state => Object.assign({}, state),
    sagas.mapDispatchToProps
)(VerifyPage);



